import clsx from 'clsx';
import { Avatar, Icon, SolidDot, Typography } from '@/components/common';
import * as React from 'react';
import { InputSend } from '@/components/chat/InputSend';
import Icons from '@/const/icons';
import { ChatAIMessageList } from '../chat-button';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';
import { InboxEmptyState } from './InboxEmptyState';
import { useChatBoxClient } from '@/hooks';
import { ChatAIProps } from './DesktopInboxList';

export const MobileInboxListV2: React.FC<ChatAIProps> = ({
	tableData,
	markAsRead,
}) => {
	const router = useRouter();
	const [idCaseSelected, setIdCaseSelected] = React.useState<string>('');
	const {
		isLoading,
		chatMessages,
		applicationId,
		conversationId,
		streamingMessageId,
		currentBotMessage,
		containerRef,
		onSubmit,
		form,
		conversationInfo,
		inputDisabled,
		addChatMessages,
		isLoadingConversation,
		conversationPaginated,
	} = useChatBoxClient();

	const currentCaseRef = React.useRef<string | null>(null);

	React.useEffect(() => {
		const totalItems = conversationPaginated?.pages?.[0]?.totalItems;
		if (
			idCaseSelected &&
			conversationId &&
			!isLoadingConversation &&
			conversationPaginated &&
			conversationInfo?.data &&
			totalItems === 0 &&
			chatMessages.length === 0 &&
			!streamingMessageId &&
			currentCaseRef.current !== idCaseSelected
		) {
			currentCaseRef.current = idCaseSelected;
			addChatMessages('', 'bot');
		}
	}, [
		idCaseSelected,
		conversationId,
		isLoadingConversation,
		conversationPaginated,
		conversationInfo,
		chatMessages.length,
		streamingMessageId,
		addChatMessages,
	]);

	return (
		<div
			className={clsx('md:hidden flex flex-col gap-2', {
				'h-[calc(100dvh-180px)]': idCaseSelected,
			})}
		>
			{idCaseSelected ? (
				<div className="flex flex-col">
					<div className="flex items-center gap-x-6 shrink-0 mb-4 px-2">
						<button
							type="button"
							onClick={() => {
								setIdCaseSelected('');
							}}
						>
							<Icon src={Icons.arrowLeft} width={24} height={24} />
						</button>
						<Typography type="semibold-14">
							{tableData.find((row) => row.id === idCaseSelected)?.case_name}
						</Typography>
					</div>
					<div className="flex-1 overflow-hidden pb-[40px]">
						<ChatAIMessageList
							messages={chatMessages}
							// messages={[
							// 	{
							// 		id: '1',
							// 		message: `<div class="flex flex-col gap-3"><div class="p-3 rounded-2xl w-fit bg-grey-10 rounded-tl-none"><div class="flex flex-col gap-4">
							// 			<p>Hello! I'm your assistant.</p>
							// 			<p>I'm happy to help you today. To provide you with the most accurate and helpful information, could you tell me a little more about the specific case or situation you'd like to discuss?</p>
							// 			<p>For example, you can tell me if it's about:</p>
							// 			<div class="flex flex-col gap-2 items-start">
							// 				<button class="bg-[#2084E726] text-[#2084E7] px-4 py-2.5 rounded-[12px] text-sm font-semibold hover:bg-[#2084E733] transition-colors text-left w-fit">A technical problem or error you're experiencing.</button>
							// 				<button class="bg-[#2084E726] text-[#2084E7] px-4 py-2.5 rounded-[12px] text-sm font-semibold hover:bg-[#2084E733] transition-colors text-left w-fit">A question about a process, procedure, or process.</button>
							// 				<button class="bg-[#2084E726] text-[#2084E7] px-4 py-2.5 rounded-[12px] text-sm font-semibold hover:bg-[#2084E733] transition-colors text-left w-fit">Any other situation where you need guidance or information.</button>
							// 			</div>
							// 		</div></div></div>`,
							// 		role: 'bot',
							// 		timestamp: new Date().toISOString(),
							// 		first_name: 'ClaimMagic',
							// 		last_name: 'AI',
							// 	},
							// ]}
							isLoading={isLoading}
							hideHeaderDate={true}
							className="max-h-[calc(100vh-340px)]"
							applicationId={applicationId}
							conversationId={conversationId}
							caseId={
								tableData.find((row) => row.id === idCaseSelected)?.case_id
							}
							containerRef={containerRef}
							streamingMessageId={streamingMessageId}
							currentBotMessage={currentBotMessage}
						/>
						{conversationInfo?.data.status === 'CLOSED' && (
							<Typography
								type="regular-14"
								className="text-opacity-black text-center mt-2"
							>
								This conversation has been resolved and closed.
							</Typography>
						)}
					</div>
					<div className="p-3 bg-white fixed bottom-0 left-0 w-full z-10 border-t shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
						<InputSend
							form={form}
							onSubmit={onSubmit}
							disabled={inputDisabled}
						/>
					</div>
				</div>
			) : (
				<div className="flex flex-col gap-2">
					<Typography type="semibold-14" className="mb-5">
						Cases
					</Typography>
					<div className="flex flex-col gap-2 pb-6">
						{tableData.length === 0 ? (
							<InboxEmptyState />
						) : (
							tableData.map((row, index) => (
								<div
									key={index}
									className={clsx(
										'px-4 py-[18px] flex items-center gap-3 rounded-xl border border-grey-100',
										row.is_read ? 'bg-white' : 'bg-green-secondary'
									)}
									onClick={async () => {
										if (!row.is_read) {
											await markAsRead(row.id);
										}
										router.push({
											pathname: AppRoutes.CLIENTAPP_INBOX,
											query: {
												id: row.id,
											},
										});
										setIdCaseSelected(row.id);
									}}
								>
									<div className="flex items-center justify-between gap-2 w-full">
										<div className="flex items-center gap-2">
											<SolidDot
												size="10px"
												color="bg-main-red"
												className={clsx(
													'shrink-0',
													row.is_read ? 'opacity-0' : 'opacity-100'
												)}
											/>
											<Avatar
												firstname={row.case_name || ''}
												lastname={''}
												photoUrl=""
												size="small"
											/>
											<Typography
												type="regular-14"
												text={`${row?.case_name}`}
											/>
										</div>
										{/* {row.count > 0 && <InboxCounterBadge count={row.count} />} */}
									</div>
								</div>
							))
						)}
					</div>
				</div>
			)}
		</div>
	);
};
